import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useNavigate, Outlet } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import type { Contact } from 'types/list'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { loadGroups } from '@disco/state-management/actions/GroupActions'
import {
  getPlaylistReport,
  resetPlaylistReportParams,
  toggleReportColumn,
  toggleReportColumnCustomFields,
  updatePlaylistReportParams,
} from '@disco/state-management/actions/ReportActions'
import {
  ANALYTICS_LIMITED,
  ANALYTICS_UNLIMITED,
  IS_ARTIST,
  REPORTS_ADVANCED,
  REPORTS_BASIC,
} from '@disco/constants/FeatureTypes'
import type { PlaylistReportState } from '@disco/state-management/reducers/reports/playlistReport'
import type { featureData } from '@disco/state-management/selectors/business'
import {
  PRO,
  useFeatureData,
  useFeatureEnabled,
} from '@disco/state-management/selectors/business'
import { getContact } from '@disco/state-management/selectors/contacts'
import {
  getGroupsWithNestingAndSorting,
  getTerritories,
} from '@disco/state-management/selectors/groups'
import { getReportOptions } from '@disco/state-management/selectors/search'
import { getCustomFields } from '@disco/state-management/selectors/user'
import PlaylistReport from './PlaylistReport'
const ACTIONS = {
  getPlaylistReport,
  loadGroups,
  resetPlaylistReportParams,
  sendMixpanelEvent,
  toggleReportColumn,
  toggleReportColumnCustomFields,
  updatePlaylistReportParams,
}

const InsightsReportsContainer = (): any => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isFeatureEnabled, isTrial }: featureData =
    useFeatureData(REPORTS_ADVANCED)
  const { isFeatureEnabled: isFeatureLimited }: featureData =
    useFeatureData(REPORTS_BASIC)
  const isAnalyticsEnabled: boolean = useFeatureEnabled(ANALYTICS_UNLIMITED)
  const isAnalyticsLimited: boolean = useFeatureEnabled(ANALYTICS_LIMITED)
  const isArtist: boolean = useFeatureEnabled(IS_ARTIST)
  useEffect(() => {
    if (isArtist) {
      navigate('/insights')
    }
  }, [isArtist, navigate])
  const channels = useSelector(getGroupsWithNestingAndSorting)
  const customFields = useSelector(getCustomFields)
  const metadataOptions = useSelector(getReportOptions)
  const territories = useSelector(getTerritories)
  const contact: Contact | null | undefined = useSelector(state =>
    getContact(state, {
      contactId: parseInt(state.reports.playlistReport.form.sharedWith),
    }),
  )
  const state: PlaylistReportState = useSelector(
    state => state.reports.playlistReport,
  )

  return (
    <>
      <PlaylistReport
        badgeTitle={PRO}
        channels={channels}
        customFields={customFields}
        customFieldsColumns={state.customFieldsColumns}
        downloadUrl={
          state.report?.results && 'downloadUrl' in state.report.results
            ? state.report.results.downloadUrl
            : undefined
        }
        form={state.form}
        metadataOptions={metadataOptions}
        playlistReportRequest={state.report}
        teamMember={contact ? contact.is_team_member : false}
        territories={territories}
        visibleColumns={state.visibleColumns}
        {...{
          insightsDisabled: !isAnalyticsEnabled && !isAnalyticsLimited,
          isFeatureEnabled,
          isFeatureLimited,
          isTrial,
        }}
        {...bindActionCreators(ACTIONS, dispatch)}
      />
      <Outlet />
    </>
  )
}

export default InsightsReportsContainer
